import * as AcceptNotification from '@ori-events/accept-notification';
import * as AddBackorderDemand from '@ori-events/add-backorder-demand';
import * as AddBasketOffer from '@ori-events/add-basket-offer';
import * as AddCatalogueOffer from '@ori-events/add-catalogue-offer';
import * as AddCustomerOrder from '@ori-events/add-customer-order';
import * as AddFreeShippingOffer from '@ori-events/add-free-shipping-offer';
import * as AddToBasket from '@ori-events/add-to-basket';
import * as AddVoucher from '@ori-events/add-voucher';
import * as BasketUpdated from '@ori-events/basket-updated';
import * as CancelBackorderDemand from '@ori-events/cancel-backorder-demand';
import * as CancelSubscription from '@ori-events/cancel-subscription';
import * as ClearBasket from '@ori-events/clear-basket';
import * as DelaySubscription from '@ori-events/delay-subscription';
import * as LoyaltyDialogResult from '@ori-events/loyalty-dialog-result';
import * as PlaceOrderRequested from '@ori-events/place-order-requested';
import * as RemoveBasketItem from '@ori-events/remove-basket-item';
import * as RemoveCustomerOrder from '@ori-events/remove-customer-order';
import * as RemoveVoucher from '@ori-events/remove-voucher';
import * as ReplaceShoppingBagItem from '@ori-events/replace-shopping-bag-item';
import * as ReplaceSubscription from '@ori-events/replace-subscription';
import * as RequestClearShoppingCache from '@ori-events/request-clear-shopping-cache';
import * as RequestShoppingContext from '@ori-events/request-shopping-context';
import * as ResetShippingSelection from '@ori-events/reset-shipping-selection';
import { ShoppingContextLoadedEvent } from '@ori-events/shopping-context-loaded';
import * as UpdateBasketItem from '@ori-events/update-basket-item';
import * as UpdateCustomerOrderItem from '@ori-events/update-customer-order-item';
import * as UpdateDelivery from '@ori-events/update-delivery';
import * as UpdateDeliveryChannel from '@ori-events/update-delivery-channel';
import * as UpdateDonation from '@ori-events/update-donation';
import * as UpdateOfferItem from '@ori-events/update-offer-item';
import * as UpdateOfferItems from '@ori-events/update-offer-items';
import * as UpdatePaymentProvider from '@ori-events/update-payment-provider';

import acceptNotificationHandler from './handlers/acceptNotificationHandler';
import addBackorderDemandHandler from './handlers/addBackorderDemandHandler';
import addBasketOfferHandler from './handlers/addBasketOfferHandler';
import addCatalogueOfferHandler from './handlers/addCatalogueOfferHandler';
import addCustomerOrderHandler from './handlers/addCustomerOrderHandler';
import addFreeShippingOfferHandler from './handlers/addFreeShippingOfferHandler';
import addToBasketHandler from './handlers/addToBasketHandler';
import addVoucherHandler from './handlers/addVoucherHandler';
import cancelBackorderDemandHandler from './handlers/cancelBackorderDemandHandler';
import cancelSubscriptionHandler from './handlers/cancelSubscriptionHandler';
import checkLoyaltyDialogResultAndAddToBasketHandler from './handlers/checkLoyaltyDialogResultAndAddToBasketHandler';
import clearBasketHandler from './handlers/clearBasketHandler';
import delaySubscriptionHandler from './handlers/delaySubscriptionHandler';
import removeCustomerOrderHandler from './handlers/removeCustomerOrderHandler';
import removeFromBasketHandler from './handlers/removeFromBasketHandler';
import removeVoucherHandler from './handlers/removeVoucherHandler';
import replaceShoppingBagItemHandler from './handlers/replaceShoppingBagItemHandler';
import replaceSubscriptionHandler from './handlers/replaceSubscriptionHandler';
import requestShoppingContextHandler from './handlers/requestShoppingContextHandler';
import resetShippingSelectionHandler from './handlers/resetShippingSelectionHandler';
import updateBasketItemHandler from './handlers/updateBasketItemHandler';
import updateCustomerOrderItemHandler from './handlers/updateCustomerOrderItemHandler';
import updateDeliveryChannelHandler from './handlers/updateDeliveryChannelHandler';
import updateDeliveryHandler from './handlers/updateDeliveryHandler';
import updateDonationHandler from './handlers/updateDonationHandler';
import updateOfferItemHandler from './handlers/updateOfferItemHandler';
import updateOfferItemsHandler from './handlers/updateOfferItemsHandler';
import updatePaymentProviderHandler from './handlers/updatePaymentProviderHandler';
import {
  contextKey,
  dispatchBasketUpdatedEvent,
  getItem,
  isPageMarkedForCacheCleanup,
  isScheduledCacheCleanup,
  isStorageAvailable,
  listenToStoredItemChanges,
  removeItem,
  removeScheduledCacheCleanup,
  setItem,
} from './helpers';
import type { AppConfiguration, UpdateEventType } from './models';
import { logDebug, logReceivedEvent, optionallyGetTokenFromUrl } from '~/utils';

interface Configuration {
  currentLanguage: string;
  customerId: number;
  globalApiUrl: string;
  identityUrl: string;
  useBrowserTabsSynchronization: boolean;
  useLocalStorage: boolean;
  tenant: string;
}

type ContextUpdateEventHandler = <T extends CustomEvent>(
  event: T,
  configuration: AppConfiguration,
) => Promise<BasketUpdated.Data | false>;

const getCachedContext = () => getItem<BasketUpdated.Data>(contextKey);
const setCachedContext = (value: BasketUpdated.Data) => setItem(contextKey, value);
const clearCachedContext = () => removeItem(contextKey);

export const contextUpdatesMap = [
  [AcceptNotification.Name, acceptNotificationHandler],
  [AddBackorderDemand.Name, addBackorderDemandHandler],
  [AddBasketOffer.Name, addBasketOfferHandler],
  [AddCatalogueOffer.Name, addCatalogueOfferHandler],
  [AddCustomerOrder.Name, addCustomerOrderHandler],
  [AddFreeShippingOffer.Name, addFreeShippingOfferHandler],
  [AddToBasket.Name, addToBasketHandler],
  [AddVoucher.Name, addVoucherHandler],
  [CancelBackorderDemand.Name, cancelBackorderDemandHandler],
  [CancelSubscription.Name, cancelSubscriptionHandler],
  [ClearBasket.Name, clearBasketHandler],
  [DelaySubscription.Name, delaySubscriptionHandler],
  [LoyaltyDialogResult.Name, checkLoyaltyDialogResultAndAddToBasketHandler],
  [RemoveBasketItem.Name, removeFromBasketHandler],
  [RemoveCustomerOrder.Name, removeCustomerOrderHandler],
  [RemoveVoucher.Name, removeVoucherHandler],
  [ReplaceShoppingBagItem.Name, replaceShoppingBagItemHandler],
  [ReplaceSubscription.Name, replaceSubscriptionHandler],
  [ResetShippingSelection.Name, resetShippingSelectionHandler],
  [UpdateBasketItem.Name, updateBasketItemHandler],
  [UpdateCustomerOrderItem.Name, updateCustomerOrderItemHandler],
  [UpdateDelivery.Name, updateDeliveryHandler],
  [UpdateDeliveryChannel.Name, updateDeliveryChannelHandler],
  [UpdateDonation.Name, updateDonationHandler],
  [UpdateOfferItems.Name, updateOfferItemsHandler],
  [UpdateOfferItem.Name, updateOfferItemHandler],
  [UpdatePaymentProvider.Name, updatePaymentProviderHandler],
] as const;

export class BasketProvider {
  static readonly CONFIG_ELEMENT_ID = 'basket-provider-data';

  private currentLanguage = '';
  private customerId = -1;
  private globalApiUrl = 'https://we-test-api.oriflame.com/uat';
  private identityUrl = 'https://identity-we-uat.oriflame.com';
  private eventListeners: Array<[string, EventListener]> = [];
  private isBrowserStorageUsed = false;
  private isBrowserTabsSynchronizationUsed = false;
  private isRequestingShoppingContext = false;
  private shoppingContext?: BasketUpdated.Data;
  private tenant = '';

  constructor() {
    this.parseConfiguration();
    if (process.env.NODE_ENV !== 'production') {
      optionallyGetTokenFromUrl(this.identityUrl);
    }
    if (this.isBrowserStorageUsed) {
      if (isPageMarkedForCacheCleanup() || isScheduledCacheCleanup()) {
        this.clearContext();
        removeScheduledCacheCleanup();
      } else {
        this.getStoredContext();
      }
    }
    this.initEventListeners();
  }

  getConfiguration(): AppConfiguration {
    const {
      currentLanguage,
      customerId,
      globalApiUrl,
      identityUrl,
      isBrowserStorageUsed,
      isBrowserTabsSynchronizationUsed,
      tenant,
    } = this;

    return {
      currentLanguage,
      customerId,
      globalApiUrl,
      identityUrl,
      isBrowserStorageUsed,
      isBrowserTabsSynchronizationUsed,
      tenant,
    };
  }

  getContext() {
    return this.shoppingContext ? { ...this.shoppingContext } : undefined;
  }

  private initEventListeners(): void {
    if (this.eventListeners.length) {
      this.removeEventListeners();
    }

    this.eventListeners = contextUpdatesMap.map(([eventName, handler]) => {
      const updateHandler = handler as ContextUpdateEventHandler;
      const action = (async <T extends CustomEvent>(event: T) => {
        logReceivedEvent(event);
        const data = await updateHandler(event, this.getConfiguration());
        if (data === false) {
          logDebug('Shopping context update failed.');
        } else {
          this.updateContext(data, BasketUpdated.Name);
        }
      }) as EventListener;

      return [eventName, action];
    });

    const requestShoppingContextListener = (async <T extends CustomEvent>(event: T) => {
      logReceivedEvent(event);

      if (this.shoppingContext) {
        logDebug(
          `Context data was loaded before. Triggering ${ShoppingContextLoadedEvent} event ...`,
        );
        dispatchBasketUpdatedEvent(this.shoppingContext, ShoppingContextLoadedEvent);

        return;
      }

      if (this.isRequestingShoppingContext) {
        logDebug('Shopping context was already requested. Waiting for response ...');

        return;
      }

      logDebug('Loading shopping context ...');
      this.isRequestingShoppingContext = true;
      const newData = await requestShoppingContextHandler();
      this.isRequestingShoppingContext = false;
      logDebug('Shopping context was loaded.');
      this.updateContext(newData, ShoppingContextLoadedEvent);
    }) as EventListener;

    this.eventListeners.push(
      // Requesting shopping context.
      [RequestShoppingContext.Name, requestShoppingContextListener],

      // This is now called when the user requests creating a new order.
      // That means that the cache should be cleared. But we don't know yet
      // if the create order action succeeds. Getting exact order creation
      // result / success might be problematic, as there are page redirects
      // involved, and after page reload, it is too late for clearing caches.
      // So current solution calls the PlaceOrderRequested event before
      // calling the request on server, so that there is enough time to
      // do this cache cleanup here before the redirects happen.
      // ATM, the redirect is being done in legacy module & this logic might
      // change after rewriting to React.
      [PlaceOrderRequested.Name, (event) => this.clearContextOnEvent(event)],

      // This is now called for other cases when clearing context data is needed.
      [RequestClearShoppingCache.Name, (event) => this.clearContextOnEvent(event)],
    );

    // If there was a change in stored data from another browser tab,
    // if the context value was set, update the context with triggering
    // basket-updated event. Other components need to be informed about
    // the new state & this will not happen on page load, but later.
    // So the basket-updated event should be fine for this.
    if (this.isBrowserTabsSynchronizationUsed) {
      const { eventName, eventListener } = listenToStoredItemChanges<BasketUpdated.Data>(
        contextKey,
        (value) => {
          if (value) {
            this.updateContext(value, BasketUpdated.Name, false);
          }
        },
      );
      this.eventListeners.push([eventName, eventListener]);
    }

    this.eventListeners.forEach(([eventName, action]) =>
      window.addEventListener(eventName, action),
    );
    logDebug('Event listeners are connected.');
  }

  removeEventListeners(): void {
    this.eventListeners.forEach(([eventName, action]) =>
      window.removeEventListener(eventName, action),
    );
    this.eventListeners = [];
    logDebug('Old event listeners were disconnected.');
  }

  private parseConfiguration(): void {
    const configElement = document.getElementById(BasketProvider.CONFIG_ELEMENT_ID);
    const rawData = configElement?.innerHTML ?? '{}';
    logDebug('Raw configuration data:\n\t', rawData);
    const { currentLanguage, customerId, globalApiUrl, identityUrl, tenant } = JSON.parse(
      rawData,
    ) as Configuration;

    this.currentLanguage = currentLanguage;
    this.customerId = customerId;
    this.globalApiUrl = globalApiUrl;
    this.identityUrl = identityUrl;
    this.tenant = tenant;

    this.isBrowserStorageUsed = isStorageAvailable;
    this.isBrowserTabsSynchronizationUsed = isStorageAvailable;

    if (configElement) {
      logDebug(`Configuration was parsed from element #${BasketProvider.CONFIG_ELEMENT_ID}.`);
    }
    logDebug('Current configuration:', {
      currentLanguage,
      customerId,
      globalApiUrl,
      identityUrl,
      tenant,
    });
  }

  private getStoredContext(): void {
    const newData = getCachedContext();
    if (!newData) {
      return;
    }
    this.updateContext(newData, undefined, false);
    logDebug('Restored context data from storage.');
  }

  private updateContext(
    newData: BasketUpdated.Data,
    eventTypeToBeTriggered?: UpdateEventType,
    shouldStoreData = true,
  ): void {
    // compare old & new state - TODO
    this.shoppingContext = newData;
    if (typeof newData !== 'undefined') {
      this.customerId = newData.customerId;
    }
    if (shouldStoreData && this.isBrowserStorageUsed) {
      setCachedContext(newData);
      logDebug('Stored context was updated with new data:\n\t', newData);
    } else {
      logDebug('Context was updated with new data:\n\t', newData);
    }
    if (eventTypeToBeTriggered) {
      logDebug(`Triggering ${eventTypeToBeTriggered} event ...`);
      dispatchBasketUpdatedEvent(newData, eventTypeToBeTriggered);
    }
  }

  private clearContext(): void {
    logDebug('Clearing stored shopping context data ...');
    this.shoppingContext = undefined;
    clearCachedContext();
  }

  private clearContextOnEvent(event: Event): void {
    logReceivedEvent(event as CustomEvent);
    this.clearContext();
  }
}

export default BasketProvider;
